import React, { FC, useState, useMemo, useEffect } from 'react'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import type { Audience, CommunicationPreferenceRule } from '@adc/sdk/types'
import { constants, Button, CheckboxField, Icon, Text } from '@adc/ui-core'
import smartsaleFavicon from '../../img/smartsale-favicon.png'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const OptStatus = graph.enums.OptStatus

export type Channel = 'EMAIL' | 'SMS' | 'PHONE_CALL'
const channelOrder = ['EMAIL', 'SMS', 'PHONE_CALL']
// Redesign shows Text before Email per the Notification Settings design.
const redesignChannelOrder = ['SMS', 'EMAIL', 'PHONE_CALL']

const mapChannelToLabelAndIconName: Record<
  Channel,
  { label: string; icon: string }
> = {
  EMAIL: { label: 'Email', icon: 'envelope' },
  SMS: { label: 'SMS', icon: 'mobile' },
  PHONE_CALL: { label: 'Call', icon: 'phone' },
}

// Redesign uses "Text" for the SMS channel per the Notification Settings design.
const redesignChannelLabels: Record<Channel, { label: string; icon: string }> =
  {
    EMAIL: { label: 'Email', icon: 'envelope' },
    SMS: { label: 'Text', icon: 'mobile' },
    PHONE_CALL: { label: 'Call', icon: 'phone' },
  }

// Mobile stacks the header away and labels each toggle inline (plural "Texts").
const mobileChannelLabels: Record<Channel, string> = {
  EMAIL: 'Email',
  SMS: 'Texts',
  PHONE_CALL: 'Call',
}

export interface CommunicationPreferencesProps {
  rules: CommunicationPreferenceRule[]
  channelsToShow: Channel[]
  mode?: 'unsubscribe' | 'settings'
  onSave?: (rules: CommunicationPreferenceRule[]) => void
  onUnsubscribeAll?: (rules: CommunicationPreferenceRule[]) => void
  isSmartSaleLead?: boolean
  /**
   * Consumer feature flags. When the flag
   * `fe.resi.enable-notification-settings-redesign` is on, the component
   * renders the redesigned Notification Settings layout (grouped section
   * cards, toggle switches, Saved Property Updates row, Sell With Us Learn
   * More state). The component owns the redesign decision so consumers don't
   * each have to read the flag.
   */
  featureFlags?: Record<string, unknown>
  /** Redesign only. Destination for the Sell With Us "Learn More" button. */
  learnMoreUrl?: string
}

const REDESIGN_FLAG = 'fe.resi.enable-notification-settings-redesign'
const DIGEST_FLAG = 'fe.resi.enable-daily-digest-preference'
// Controls whether the SMS/Text column shows for the redesigned Notification
// Settings top-section rows (Buyer / Digest / Saved Property Updates).
// Off (default) = Email-only; on = Email + SMS. Independent of the digest flag:
// whenever the redesign is on, these rows' SMS visibility is driven solely by
// this flag.
const REDESIGN_SMS_FLAG = 'fe.resi.enable-redesign-sms-channel'

// The DIGEST audience is emitted by resi-ms-notify and exists in the graph
// enum, but the repo-wide @adc/sdk hasn't been bumped to include it yet (a
// yarn.lock change currently breaks the DS Docker build's native module
// compile). Use the string value directly for now; switch to
// graph.enums.Audience.Digest once the SDK is upgraded.
const DIGEST_AUDIENCE = 'DIGEST' as const

type Topic =
  | Audience.Buyer
  | Audience.Seller
  | Audience.SavedProperties
  | typeof DIGEST_AUDIENCE

/** Titles and subtitles for each topic (by audience key for now...) */
const TOPIC_LABELS: Record<Topic, { title: string; subtitle: string }> = {
  [graph.enums.Audience.Buyer]: {
    title: 'Property Recommendations',
    subtitle:
      'Get property recommendations, auction reminders, and events in your area',
  },
  [graph.enums.Audience.SavedProperties]: {
    title: 'Saved Property Updates',
    subtitle: 'Important changes about your saved properties',
  },
  [graph.enums.Audience.Seller]: {
    title: 'SmartSale',
    subtitle: 'Notifications about selling with Auction.com',
  },
  [DIGEST_AUDIENCE]: {
    title: 'Your Daily Deals',
    subtitle:
      'New listings and important updates based on your criteria and activity',
  },
}

// Redesign-only copy overrides (keeps the legacy/flag-off fallback unchanged).
const REDESIGN_TOPIC_LABELS: Partial<
  Record<Topic, { title: string; subtitle: string }>
> = {
  [graph.enums.Audience.Seller]: {
    title: 'Sell With Us',
    subtitle:
      'Our private listing program for homeowners who want to sell with Auction.com',
  },
}

// When the Daily Digest flag is on, the global opt-in (Buyer) is renamed.
const DIGEST_BUYER_LABEL = {
  title: 'Buyer Resources and Featured Listings',
  subtitle: 'Practical how-tos, market insights, and listings worth a look',
}

const getRedesignLabel = (topic: Topic, digestEnabled: boolean) => {
  if (digestEnabled && topic === graph.enums.Audience.Buyer) {
    return DIGEST_BUYER_LABEL
  }
  return REDESIGN_TOPIC_LABELS[topic] ?? TOPIC_LABELS[topic]
}

/** Redesign section grouping (order matters). */
type RedesignSection = {
  title: string
  topics: Topic[]
  kind: 'buy' | 'sell'
}
const getRedesignSections = (digestEnabled: boolean): RedesignSection[] => [
  {
    title: 'Buying Opportunities',
    // Daily Deal Discovery (DIGEST) sits between Property Recommendations and
    // Saved Property Updates when the digest flag is on.
    topics: digestEnabled
      ? [
          graph.enums.Audience.Buyer,
          DIGEST_AUDIENCE,
          graph.enums.Audience.SavedProperties,
        ]
      : [graph.enums.Audience.Buyer, graph.enums.Audience.SavedProperties],
    kind: 'buy',
  },
  {
    title: 'Sell Your Property with Auction.com',
    topics: [graph.enums.Audience.Seller],
    kind: 'sell',
  },
]

/**
 * CommunicationPreferences
 * Renders a flexbox grid of topics (by audience) and channels with checkboxes for opt-in/out.
 * Allows bulk unchecking or unsubscribing, and saving preferences.
 */
const CommunicationPreferences: FC<CommunicationPreferencesProps> = ({
  rules,
  channelsToShow,
  // @ts-ignore -- leaving this here just in case we decide to resurrect it later - even though not needed now.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  mode = 'settings',
  onSave,
  onUnsubscribeAll,
  isSmartSaleLead = false,
  featureFlags,
  learnMoreUrl = 'https://www.smartsale.com/sell/?marketing_source=notification_settings',
}) => {
  const redesign = Boolean(featureFlags?.[REDESIGN_FLAG])
  const digestEnabled = Boolean(featureFlags?.[DIGEST_FLAG])
  const redesignSmsEnabled = Boolean(featureFlags?.[REDESIGN_SMS_FLAG])

  // Derive seller context from the fetched rules: if there's no SELLER-audience
  // rule, the user has no associated listings, so the Sell With Us row shows
  // the "Learn More" CTA instead of channel toggles. Avoids a separate
  // associated-listing-count query in consumers.
  const hasSellerListing = useMemo(
    () => rules.some(rule => rule?.audience === graph.enums.Audience.Seller),
    [rules]
  )
  // Determine which topics to show based on isSmartSaleLead and existing opt-ins
  const topics = useMemo(() => {
    if (redesign) {
      const redesignTopics = getRedesignSections(digestEnabled).flatMap(
        section => section.topics
      )
      // Learn More state (no SELLER rule => no associated listing): the Sell
      // With Us row renders a CTA, not toggles. Don't synthesize SELLER rules —
      // writing them (opted_out) would make a SELLER rule exist and flip the
      // row out of Learn More on reload.
      return hasSellerListing
        ? redesignTopics
        : redesignTopics.filter(topic => topic !== graph.enums.Audience.Seller)
    }

    const allTopics = Object.keys(TOPIC_LABELS)
      .map(key => key as Topic)
      // SavedProperties and Digest only exist in the redesigned layout.
      .filter(
        topic =>
          topic !== graph.enums.Audience.SavedProperties &&
          topic !== DIGEST_AUDIENCE
      )

    if (!isSmartSaleLead) {
      return allTopics
    }

    // Check if there are any non-Seller OPT_IN rules
    const hasNonSellerOptIns = rules.some(
      rule =>
        rule.audience !== graph.enums.Audience.Seller &&
        rule.opt_status === OptStatus.OptedIn
    )

    // If isSmartSaleLead and no non-Seller opt-ins, only show Seller
    if (!hasNonSellerOptIns) {
      return allTopics.filter(topic => topic === graph.enums.Audience.Seller)
    }

    return allTopics
  }, [rules, isSmartSaleLead, redesign, digestEnabled, hasSellerListing])

  // show channels in same order (left to right) always
  const sortedChannels = useMemo(() => {
    const order = redesign ? redesignChannelOrder : channelOrder
    return [...channelsToShow].sort(
      (a, b) => order.indexOf(a) - order.indexOf(b)
    )
  }, [channelsToShow, redesign])

  // Ensure all topic/channel pairs exist in localRules, defaulting to OPTED_OUT if missing
  const initialRules = useMemo((): CommunicationPreferenceRule[] => {
    const ruleMap = new Map<string, CommunicationPreferenceRule>()
    for (const rule of rules) {
      if (rule.audience && rule.channel) {
        ruleMap.set(`${String(rule.audience)}|${String(rule.channel)}`, rule)
      }
    }
    const allAvailable: CommunicationPreferenceRule[] = []
    topics.forEach(topic => {
      sortedChannels.forEach(channel => {
        const key = `${topic}|${channel}`
        if (ruleMap.has(key)) {
          const rule = ruleMap.get(key)
          if (rule) {
            allAvailable.push(rule)
          }
        } else {
          allAvailable.push({
            // 'DIGEST' isn't in the installed sdk Audience enum yet.
            audience: topic as CommunicationPreferenceRule['audience'],
            // @ts-ignore next
            channel,
            opt_status: OptStatus.OptedOut,
          })
        }
      })
    })
    return allAvailable
  }, [rules, topics, sortedChannels])

  const [localRules, setLocalRules] =
    useState<CommunicationPreferenceRule[]>(initialRules)

  // Keep localRules in sync if rules prop changes
  useEffect(() => {
    setLocalRules(initialRules)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, topics.join(','), sortedChannels.join(',')])

  const getOptStatus = (topic: string, channel: string) => {
    return (
      localRules.find(
        rule => rule.audience === topic && rule.channel === channel
      )?.opt_status === OptStatus.OptedIn
    )
  }

  const toggleRules = (
    rules: CommunicationPreferenceRule[],
    topic: string,
    channel: string
  ) =>
    rules.map(rule =>
      rule.audience === topic && rule.channel === channel
        ? {
            ...rule,
            opt_status:
              rule.opt_status === OptStatus.OptedIn
                ? OptStatus.OptedOut
                : OptStatus.OptedIn,
          }
        : rule
    )

  const handleToggle = (topic: string, channel: string) => {
    setLocalRules(prev => toggleRules(prev, topic, channel))
  }

  // Redesign has no explicit "Update" button — toggles persist immediately.
  const handleToggleAndSave = (topic: string, channel: string) => {
    const next = toggleRules(localRules, topic, channel)
    setLocalRules(next)
    if (onSave) {
      onSave(next)
    }
  }

  const handleSave = () => {
    if (onSave) {
      onSave(localRules)
    }
  }

  /**
   * We'll do the logic here to determine what unsubscribing from all really means...
   * which is, set all rules to opt-out, but only for channels that are shown.
   */
  const handleUnsubscribeAll = () => {
    if (!onUnsubscribeAll) return
    // Redesign: "Unsubscribe from All Marketing Emails" only opts out of EMAIL,
    // never SMS. Legacy "Unsubscribe All" clears every shown channel.
    const channelsToClear: Channel[] = redesign ? ['EMAIL'] : channelsToShow
    const updatedRules: NonNullable<CommunicationPreferenceRule[]> =
      localRules.map(rule => {
        if (
          rule?.channel &&
          channelsToClear.includes(rule.channel as Channel)
        ) {
          return { ...rule, opt_status: graph.enums.OptStatus.OptedOut }
        }
        return rule
      }) || []
    setLocalRules(updatedRules)
    onUnsubscribeAll(updatedRules)
  }

  if (redesign) {
    return (
      <div className={cx('communication-preferences', 'redesign')}>
        {getRedesignSections(digestEnabled).map(section => {
          const isSellLearnMore = section.kind === 'sell' && !hasSellerListing
          // The entire Text/SMS column under Buying Opportunities is hidden
          // behind fe.resi.enable-redesign-sms-channel. When off,
          // drop SMS from this section entirely — no header, no cells — since
          // no Buying Opportunities row has an SMS option. Sell With Us keeps
          // its Text column unchanged.
          const sectionChannels =
            section.kind === 'buy' && !redesignSmsEnabled
              ? sortedChannels.filter(channel => channel !== 'SMS')
              : sortedChannels

          return (
            <section
              className={cx('pref-section')}
              key={section.title}
              aria-label={section.title}
            >
              <Text
                as='h3'
                size='huge'
                weight='heavy'
                className={cx('section-title')}
              >
                {section.title}
              </Text>
              <div className={cx('section-card')}>
                {!isSellLearnMore && (
                  <div className={cx('channel-header-row')} role='row'>
                    <div className={cx('topic-col')} />
                    {sectionChannels.map(channel => (
                      <div className={cx('channel-col-header')} key={channel}>
                        <Icon
                          name={redesignChannelLabels[channel].icon}
                          iconStyle='solid'
                          size='medium'
                          fitted
                        />
                        <span className={cx('channel-header-label')}>
                          {redesignChannelLabels[channel].label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {section.topics.map(topic => (
                  <div className={cx('pref-row')} key={topic} role='row'>
                    <div className={cx('topic-col')}>
                      <Text
                        size='large'
                        weight='heavy'
                        color={constants.ColorNames.GrayDark}
                        className={cx('topic-title')}
                      >
                        {getRedesignLabel(topic, digestEnabled)?.title || topic}
                      </Text>
                      <Text
                        size='small'
                        color={constants.ColorNames.GrayMedium}
                      >
                        {getRedesignLabel(topic, digestEnabled)?.subtitle}
                      </Text>
                    </div>

                    {isSellLearnMore ? (
                      <Button
                        role='secondary'
                        tag='a'
                        link={learnMoreUrl}
                        className={cx('learn-more-btn')}
                        elmId='sell_learn_more_button'
                      >
                        Learn More
                        <img
                          src={smartsaleFavicon}
                          alt=''
                          aria-hidden
                          className={cx('learn-more-icon')}
                        />
                      </Button>
                    ) : (
                      <div className={cx('channels-group')}>
                        {sectionChannels.map(channel => {
                          // Within Buying Opportunities, only Saved Property
                          // Update Alert has an SMS toggle. The other rows
                          // (Product Recommendations / Tips & Trends, Daily
                          // Deal Discovery) render an empty SMS cell to keep
                          // the grid aligned. Sell With Us is unaffected.
                          const hideSmsToggle =
                            channel === 'SMS' &&
                            section.kind === 'buy' &&
                            topic !== graph.enums.Audience.SavedProperties
                          return (
                            <div
                              className={cx(
                                'channel-cell',
                                `channel-cell--${channel.toLowerCase()}`
                              )}
                              key={channel}
                            >
                              {!hideSmsToggle && (
                                <>
                                  <CheckboxField
                                    toggle
                                    className={cx('toggle')}
                                    aria-label={`Opt in to ${redesignChannelLabels[
                                      channel
                                    ].label.toLowerCase()} for ${topic}`}
                                    checked={getOptStatus(topic, channel)}
                                    onChange={() =>
                                      handleToggleAndSave(topic, channel)
                                    }
                                    data-elm-id={`${topic.toLowerCase()}_${channel.toLowerCase()}_toggle`}
                                  />
                                  <span className={cx('inline-channel-label')}>
                                    {mobileChannelLabels[channel]}
                                  </span>
                                </>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )
        })}

        <section className={cx('pref-section', 'static-section')}>
          <Text
            as='h3'
            size='huge'
            weight='heavy'
            className={cx('section-title')}
          >
            Account &amp; Policy Updates
          </Text>
          <Text size='small' color={constants.ColorNames.GrayMedium}>
            You will always receive emails on account security and required
            legal notices
          </Text>
        </section>

        <section className={cx('pref-section', 'unsubscribe-section')}>
          <Text
            as='h3'
            size='huge'
            weight='heavy'
            className={cx('section-title')}
          >
            Unsubscribe from All Marketing Emails
          </Text>
          <div className={cx('unsubscribe-copy')}>
            <Text
              as='span'
              size='small'
              color={constants.ColorNames.GrayMedium}
            >
              Opt out of all email marketing and non-essential communications.
            </Text>{' '}
            <Text
              as='span'
              size='small'
              color={constants.ColorNames.GrayMedium}
              className={cx('unsubscribe-copy-rest')}
            >
              This will turn off all optional email communications including
              saved searches, property recommendations, deals, and marketing
              updates. You may continue to receive required account,
              transaction, and legal messages.
            </Text>
          </div>
          <Button
            role='secondary'
            size='small'
            color={constants.ColorNames.Gray}
            onClick={handleUnsubscribeAll}
            className={cx('unsubscribe-btn')}
            aria-label='Unsubscribe from all marketing emails'
            elmId='unsubscribe_all_button'
          >
            Unsubscribe from All Marketing Emails
          </Button>
        </section>
      </div>
    )
  }

  return (
    <div className={cx('communication-preferences')}>
      <div className={cx('preferences-grid')} role='grid'>
        <div className={cx('grid-header', 'row')}>
          <div className={cx('topic-col', 'header-cell')}> </div>
          {sortedChannels.map(channel => (
            <div className={cx('channel-header')} key={channel}>
              <Icon
                name={mapChannelToLabelAndIconName[channel].icon}
                iconStyle='regular'
                size='large'
                fitted
              />
              <div key={channel} className={cx('channel-label', 'channel-col')}>
                {mapChannelToLabelAndIconName[channel].label}
              </div>
            </div>
          ))}
        </div>
        {topics.map(topic => (
          <div className={cx('row')} key={topic} role='row'>
            <div className={cx('topic-col', 'body-cell')}>
              <Text
                size='large'
                weight='heavy'
                color={constants.ColorNames.Gray}
                style={{ paddingBottom: 6 }}
              >
                {TOPIC_LABELS[topic]?.title || topic}
              </Text>
              <Text size='small' color={constants.ColorNames.GrayMedium}>
                {TOPIC_LABELS[topic]?.subtitle}
              </Text>
            </div>
            {sortedChannels.map(channel => (
              <div className={cx('channel-col', 'body-cell')} key={channel}>
                <CheckboxField
                  className={cx('checkbox')}
                  aria-label={`Opt in to ${channel.replace(
                    '_',
                    ' '
                  )} for ${topic}`}
                  checked={getOptStatus(topic, channel)}
                  onChange={() => handleToggle(topic, channel)}
                  data-elm-id={`${topic.toLowerCase()}_${channel.toLocaleLowerCase()}_checkbox`}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className={cx('actions-row')}>
        <Button
          role='secondary'
          color={constants.ColorNames.Gray}
          onClick={handleUnsubscribeAll}
          className={cx('unsubscribe-btn')}
          aria-label='Unsubscribe from all'
          elmId='unsubscribe_all_button'
        >
          Unsubscribe All
        </Button>

        <Button
          role='secondary'
          onClick={handleSave}
          className={cx('save-btn')}
          aria-label='Save communication preferences'
          elmId='update_button'
        >
          Update
        </Button>
      </div>
    </div>
  )
}

export default CommunicationPreferences
